/**
 * StepTechCriteria — Etapa: Critérios Técnicos (Readiness).
 *
 * Exibe o score de prontidão baseado no que foi persistido nas etapas anteriores.
 * A readiness é calculada verificando quais recursos foram preenchidos:
 *   - Projeto criado (+15%)
 *   - Git repo adicionado (+15%)
 *   - Discovery context (+20%)
 *   - Documentos (+15%)
 *   - Data sources de negócio (+20%)
 *   - Tech blueprint (+15%) — não obrigatório neste wizard (etapa de tecnologia)
 *
 * Sem mock hardcoded: todos os valores vêm dos dados reais das queries anteriores
 * ou do projectId no JourneyContext.
 *
 * Paleta C, estilos inline, pt-BR, a11y.
 */
import { MOSTRAR_ENGINEERING_STANDARDS } from '@/lib/engineeringStandards';
import { CheckCircle2, AlertCircle, AlertTriangle, ShieldCheck, ChevronRight, Pencil } from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, Badge } from '@/components/ui';
import { QueryError, LoadingSpinner } from '@/components/ui/QueryState';
import {
  useProject,
  useDiscoveryContexts,
  useProjectDocuments,
  useDataSources,
  useIdeation,
  useAddProjectDocument,
  useAddDataSource,
  useSuggestBusinessScope,
} from '@/api/projects';
import { useGitRepositories } from '@/api/git';
import { useState } from 'react';
import { useJourney } from '../JourneyContext';
import {
  useDecideJourneyGate, useTechBlueprint, useArtifacts, useComponents,
} from '@/api/techScope';

// ─── ResolveCriterion — corrigir um item parcial com IA OU com o próprio texto ──
// Fluxo: "Gerar com IA" PREENCHE o editor (não salva) → o usuário REVISA/EDITA/
// argumenta → "Usar este texto" salva o que estiver ali. A escolha é sempre aceita.
function ResolveCriterion({ label, hint, onGenerate, onSave, saving, error }) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genErr, setGenErr] = useState(false);
  const busy = generating || saving;

  async function handleGenerate() {
    setGenErr(false); setGenerating(true);
    try {
      const t = await onGenerate();
      setText((t || '').trim());
      setOpen(true);       // mostra o texto da IA no editor p/ revisão
    } catch { setGenErr(true); }
    finally { setGenerating(false); }
  }

  return (
    <div style={{ padding: '12px 0', borderBottom: `1px solid ${C.blackBorder}` }}>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: C.neutral, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12, color: C.neutralDim, marginBottom: 8 }}>{hint}</div>
      <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
        <Btn variant="outline" size="sm" onClick={handleGenerate} disabled={busy} aria-busy={generating}>
          {generating ? 'Gerando com IA…' : '✨ Gerar com IA (revisar antes)'}
        </Btn>
        <Btn variant="ghost" size="sm" onClick={() => setOpen(true)} disabled={busy}>
          ✍️ Escrever eu mesmo
        </Btn>
      </div>
      {open && (
        <div style={{ marginTop: 10 }}>
          <textarea value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Revise/edite o texto da IA — ou escreva o seu. Será salvo exatamente como você deixar."
            rows={10} style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical',
              background: C.blackElev2, color: C.neutral, border: `1px solid ${C.blackBorder}`,
              borderRadius: 6, padding: 10, fontSize: 13, lineHeight: 1.5, fontFamily: 'inherit' }} />
          <div className="flex items-center gap-2" style={{ marginTop: 8, flexWrap: 'wrap' }}>
            <Btn variant="primary" size="sm" onClick={() => onSave(text)} disabled={!text.trim() || busy}>
              {saving ? 'Salvando…' : '✅ Usar este texto'}
            </Btn>
            <span style={{ fontSize: 11.5, color: C.neutralMuted }}>
              edite o texto da IA como quiser antes de salvar — o que você deixar é o que vale
            </span>
          </div>
        </div>
      )}
      <div style={{ fontSize: 11.5, color: C.neutralMuted, marginTop: 8 }}>
        Sua escolha é sempre aceita — o texto da IA fica editável e você decide.
      </div>
      {(error || genErr) && <div style={{ fontSize: 12, color: C.hot, marginTop: 6 }}>Falha. Tente novamente.</div>}
    </div>
  );
}

// ─── CriterionRow — accordion técnico (impacto se pendente + Ajustar) ──────────
const STATUS_META = {
  ok:      { Icon: CheckCircle2,  color: C.cyber,   label: 'Completo'     },
  partial: { Icon: AlertTriangle, color: C.digital, label: 'Parcial'      },
  missing: { Icon: AlertCircle,   color: C.hot,     label: 'Insuficiente' },
};

function CriterionRow({ area, status, description, impact, blocking, sev, step, onAjustar }) {
  const meta = STATUS_META[status] ?? STATUS_META.missing;
  const [open, setOpen] = useState(status !== 'ok');
  const { Icon, color } = meta;
  return (
    <div style={{ background: C.black, borderRadius: 6, border: `1px solid ${C.blackBorder}`,
      borderLeft: `3px solid ${color}`, marginBottom: 8 }}>
      <button onClick={() => setOpen(!open)} aria-expanded={open}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
          cursor: 'pointer', textAlign: 'left', background: 'transparent', border: 'none' }}>
        <Icon size={16} style={{ color, flexShrink: 0 }} aria-hidden="true" />
        <span style={{ fontSize: 14, color: C.neutral, fontWeight: 500, flex: 1, minWidth: 0 }}>{area}</span>
        {sev && <Badge color={sev === 'ALTO' ? C.hot : sev === 'MÉDIO' ? C.digital : C.neutralMuted} light>{sev}</Badge>}
        <Badge color={color} light>{meta.label}</Badge>
        <Badge color={blocking ? C.hot : C.neutralMuted} light>{blocking ? 'IMPEDITIVO' : 'NÃO-IMPEDITIVO'}</Badge>
        <ChevronRight size={15} style={{ color: C.neutralDim, flexShrink: 0,
          transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 150ms' }} />
      </button>
      {open && (
        <div style={{ padding: '0 14px 14px 40px' }}>
          <div style={{ fontSize: 12, color: C.neutralDim, lineHeight: 1.4, marginBottom: 10 }}>{description}</div>
          {impact && (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: C.neutralMuted, marginBottom: 4 }}>
                IMPACTO SE PENDENTE
              </div>
              <div style={{ fontSize: 13, color: C.neutralDim, lineHeight: 1.5, marginBottom: 12 }}>{impact}</div>
            </>
          )}
          {step && onAjustar && (
            <button onClick={() => onAjustar(step)} title={`Ajustar ${area}`}
              style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 500,
                color: status === 'ok' ? C.neutralDim : color,
                border: `1px solid ${status === 'ok' ? C.blackBorder : `${color}55`}`, borderRadius: 5,
                padding: '5px 10px', cursor: 'pointer',
                background: status === 'ok' ? 'transparent' : `${color}11` }}>
              <Pencil size={12} /> Ajustar
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── StepTechCriteria ─────────────────────────────────────────────────────────

export function StepTechCriteria({ onNext, onBack }) {
  const { projectId, goToStep } = useJourney();
  const waiverMut = useDecideJourneyGate(projectId, 'tecnologia');
  const [waiverReason, setWaiverReason] = useState('');

  const projectQuery  = useProject(projectId);
  const reposQuery    = useGitRepositories(projectId);
  const contextsQuery = useDiscoveryContexts(projectId);
  const docsQuery     = useProjectDocuments(projectId);
  const sourcesQuery  = useDataSources(projectId);
  const ideationQuery = useIdeation(projectId);
  // Memória da Fase 3 (Tecnologia): blueprint, artefatos e componentes de reuso.
  const blueprintQuery  = useTechBlueprint(projectId);
  const artifactsQuery  = useArtifacts(projectId);
  const componentsQuery = useComponents(projectId);

  // projectQuery é auxiliar (só para inferir greenfield) — não bloqueia nem
  // dispara banner de erro: os critérios são derivados das demais queries.
  const isLoading = [reposQuery, contextsQuery, docsQuery, sourcesQuery]
    .some((q) => q.isLoading);
  const hasError  = [reposQuery, contextsQuery, docsQuery, sourcesQuery]
    .some((q) => q.isError);

  // Ideação como fonte de escopo: o trabalho das etapas anteriores CONTA no score.
  // Uma Ideação rica (funcionalidades/histórias) satisfaz "Documentos & Histórias";
  // com valor/regras de negócio satisfaz "Data Sources de Negócio".
  const idea = ideationQuery.data || {};
  const _nonEmpty = (v) => {
    if (Array.isArray(v)) return v.length > 0;
    const s = String(v ?? '').trim();
    return s.length > 0 && s !== '[]';
  };
  const ideaHasStories  = _nonEmpty(idea.features) || _nonEmpty(idea.main_idea);
  const ideaHasBusiness = _nonEmpty(idea.business_value) || _nonEmpty(idea.business_rules);

  // Derivar status de cada critério
  const hasRepo     = (reposQuery.data?.length ?? 0) > 0;
  // BROWNFIELD: um repositório INDEXADO é fonte de contexto/documentação/dados —
  // o código-fonte + README + esquema cobrem "documentos & histórias", "contexto"
  // e o entendimento das "fontes de negócio". Sem isso, o passo acusava
  // erroneamente "sem documentos/histórias/data sources" mesmo com o repo conectado.
  // INDEXADO = pronto E com trechos (2026-09-23). `indexed_docs > 0` sozinho aceitava
  // um README indexado — ou a fixture simulada — como repositório inteiro.
  const repoIndexed = (reposQuery.data ?? []).some(
    (r) => r.knowledge_pack_status === 'ready' && (r.indexed_docs ?? 0) > 0,
  );
  const hasContextRaw = (contextsQuery.data?.length ?? 0) > 0;
  const hasContext  = hasContextRaw || repoIndexed;
  const docsUploaded = (docsQuery.data?.length ?? 0) > 0;
  const hasDocs     = docsUploaded || ideaHasStories || repoIndexed;   // Ideação/repo cobrem histórias
  const bizSources  = (sourcesQuery.data ?? []).filter((s) => !s.kind || s.kind === 'business');
  const sourcesAdded = bizSources.length > 0;
  const hasSources  = sourcesAdded || ideaHasBusiness || repoIndexed;  // Ideação/repo cobrem o negócio

  // Contagens reais → detalhe do "por que parcial".
  const docsCount    = docsQuery.data?.length ?? 0;
  const sourcesCount = bizSources.length;
  const contextCount = contextsQuery.data?.length ?? 0;

  // ── Memória técnica da Fase 3 (blueprint, artefatos, componentes de reuso) ──
  // Os critérios técnicos consideram TUDO que já está na memória até aqui.
  const bp = blueprintQuery.data || {};
  const bpLangs = bp.languages ?? [];
  const bpFw = bp.frameworks ?? [];
  // GET /tech-blueprint é snake_case (target_architecture); toleramos camelCase
  // caso venha do POST /generate.
  const bpArch = bp.target_architecture ?? bp.targetArchitecture ?? '';
  const hasBlueprint = _nonEmpty(bpArch) || bpLangs.length > 0 || bpFw.length > 0;
  const artifacts = artifactsQuery.data?.artifacts ?? [];
  const artifactsCount = artifacts.length;
  const components = componentsQuery.data?.components ?? [];
  const reusedCount = components.filter((c) => c.selected).length;
  const componentsEvaluated = components.length > 0;   // biblioteca já buscada/validada
  // O tipo vem do PROJETO. "Tem ideação ⇒ greenfield" transformava um brownfield com
  // qualquer ideação em greenfield e o repositório deixava de ser obrigatório.
  const isGreenfield = projectQuery.data?.project_type === 'greenfield';
  // Segurança/Compliance: heurística sobre o que já está na memória (blueprint + ideação).
  const _secText = `${String(bpArch || '')} ${String(idea.non_functional_requirements || '')}`.toLowerCase();
  const hasSecurity = /lgpd|segur|compliance|criptograf|owasp|stride|autentic|rbac|bacen/.test(_secText);

  // Resolver itens parciais (docs/sources) — IA ou texto do usuário, sempre aceito.
  const addDoc       = useAddProjectDocument(projectId);
  const addSource    = useAddDataSource(projectId);
  const suggestScope = useSuggestBusinessScope(projectId);

  // "Gerar com IA" → retorna o texto (o painel mostra p/ revisão; NÃO salva aqui).
  async function generateScopeText() {
    const r = await suggestScope.mutateAsync();
    return (r?.suggestion || '').trim();
  }
  // "Usar este texto" → salva o que o usuário deixou (texto da IA revisado ou o dele).
  async function saveDocsText(text) {
    const t = String(text || '').trim();
    if (!t) return;
    await addDoc.mutateAsync({
      filename: 'historias-e-contexto.md', doc_tag: 'stories',
      content_text: t, size_bytes: t.length, content_type: 'text/markdown',
    });
  }
  async function saveSourcesText(text) {
    const t = String(text || '').trim();
    if (!t) return;
    await addSource.mutateAsync({
      name: (t.split('\n')[0] || 'Fonte de negócio').slice(0, 120),
      type: 'outros', kind: 'business', note: t,
    });
  }

  const CRITERIA = [
    {
      id: 'repo', area: 'Repositório & Git', step: 'git',
      // Brownfield exige o repositório INDEXADO — a execução é barrada sem ele.
      status: isGreenfield
        ? (hasRepo ? 'ok' : 'partial')
        : (repoIndexed ? 'ok' : (hasRepo ? 'partial' : 'missing')),
      blocking: !isGreenfield, sev: 'ALTO', weight: 15,
      description: hasRepo
        ? `Repositório conectado${repoIndexed ? ' e indexado (grafo/RAG pronto)' : ' (indexação pendente)'}.`
        : isGreenfield
          ? 'Greenfield: o repositório é criado na execução — não obrigatório aqui.'
          : 'Nenhum repositório conectado. Em brownfield o código existente é a base do trabalho.',
      impact: 'Sem o repositório indexado o squad desconhece o código existente e regenera módulos já prontos (+tokens).',
    },
    {
      id: 'blueprint', area: 'Blueprint técnico', step: 'blueprint',
      status: hasBlueprint ? 'ok' : 'missing', blocking: true, sev: 'ALTO', weight: 20,
      description: hasBlueprint
        ? `Stack decidida: ${[...bpLangs, ...bpFw].slice(0, 6).join(', ') || 'definida'}.`
        : 'Blueprint técnico ainda não gerado (linguagens/frameworks/infra/DB).',
      impact: 'Sem blueprint o squad escolhe a stack por conta própria, com alto risco de divergir dos padrões corporativos.',
    },
    {
      id: 'artifacts', area: 'Artefatos técnicos', step: 'artifacts',
      status: artifactsCount > 0 ? 'ok' : 'partial', blocking: false, sev: 'MÉDIO', weight: 10,
      description: artifactsCount > 0
        ? `${artifactsCount} artefato(s) gerado(s) (ADRs/C4/topologia/Terraform).`
        : 'Nenhum artefato técnico gerado ainda.',
      impact: 'Sem ADRs/C4/topologia a decisão de arquitetura fica sem rastreabilidade no Gate de Tecnologia.',
    },
    {
      id: 'components', area: 'Biblioteca de Componentes (reuso)', step: 'components',
      status: reusedCount > 0 ? 'ok' : 'partial', blocking: false, sev: 'BAIXO', weight: 5,
      description: reusedCount > 0
        ? `${reusedCount} componente(s) de reuso confirmado(s) — vão para a execução.`
        : componentsEvaluated
          ? 'Biblioteca avaliada — nenhum componente marcado para reuso.'
          : 'Reuso ainda não avaliado (busque componentes de outros projetos).',
      impact: 'Ignorar componentes prontos e testados aumenta o custo de tokens e o risco de retrabalho.',
    },
    {
      id: 'context', area: 'Contexto & Discovery', step: 'context',
      status: hasContext ? 'ok' : 'partial', blocking: false, sev: 'MÉDIO', weight: 10,
      description: hasContextRaw
        ? `${contextCount} nota(s) de discovery/contexto salvas.`
        : repoIndexed
          ? 'Coberto pelo repositório conectado (código-fonte indexado).'
          : 'Nenhuma nota de discovery/contexto salva ainda.',
      impact: 'Contexto incompleto reduz a precisão dos requisitos e aumenta clarificações nos gates.',
    },
    {
      id: 'docs', area: 'Documentos & Histórias', step: 'context',
      status: hasDocs ? 'ok' : 'partial', blocking: false, sev: 'MÉDIO', weight: 10,
      description: docsUploaded
        ? `${docsCount} documento(s) enviado(s) (histórias/UX/compliance, incl. Swagger).`
        : ideaHasStories
          ? 'Coberto pela Ideação (funcionalidades/histórias definidas).'
          : repoIndexed
            ? 'Coberto pelo repositório conectado (código-fonte + README indexados).'
            : 'Sem documentos nem histórias na Ideação — resolva abaixo.',
      impact: 'Sem os documentos de apoio, critérios de aceite ficam genéricos e a conferência final perde rastreabilidade.',
    },
    {
      id: 'sources', area: 'Data Sources (RAG técnico)', step: 'context',
      status: hasSources ? 'ok' : 'partial', blocking: false, sev: 'MÉDIO', weight: 10,
      description: sourcesAdded
        ? `${sourcesCount} fonte(s) indexada(s) para RAG.`
        : ideaHasBusiness
          ? 'Coberto pela Ideação (valor/regras de negócio definidos).'
          : repoIndexed
            ? 'Coberto pelo repositório conectado (esquema/entidades indexados).'
            : 'Sem fontes indexadas — resolva abaixo.',
      impact: 'Sem RAG técnico o squad não respeita ADRs nem contratos de API existentes, gerando retrabalho de integração.',
    },
    {
      id: 'security', area: 'Segurança & Compliance', step: 'blueprint',
      status: hasSecurity ? 'ok' : 'partial', blocking: false, sev: 'ALTO', weight: 20,
      description: hasSecurity
        ? 'Requisitos de segurança/LGPD presentes no blueprint/escopo.'
        : 'Sem classificação de dados nem requisitos de segurança explícitos; LGPD apenas parcial.',
      impact: MOSTRAR_ENGINEERING_STANDARDS
        ? 'O STRIDE fica incompleto e o Compliance Check pode reprovar no Gate Final, bloqueando o PR da entrega.'
        : 'O STRIDE fica incompleto e os requisitos de segurança e LGPD chegam ao código sem definição.',
    },
  ];

  // Score = soma dos pesos dos critérios com status 'ok'
  const score = CRITERIA.reduce(
    (acc, c) => acc + (c.status === 'ok' ? c.weight : c.status === 'partial' ? c.weight / 2 : 0),
    0,
  );

  // Bloqueia avanço se algum critério impeditivo está missing
  const hasBlocker = CRITERIA.some((c) => c.blocking && c.status !== 'ok');

  const scoreColor = score >= 80 ? C.cyber : score >= 50 ? C.digital : C.hot;

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: C.neutral, margin: '0 0 6px' }}>
          Critérios Técnicos
        </h2>
        <p style={{ fontSize: 14, color: C.neutralDim, margin: 0 }}>
          Prontidão técnica do projeto — cada critério considera tudo que já está na
          memória (Blueprint, Artefatos, Componentes, Contexto). Critérios impeditivos
          bloqueiam o Gate de Tecnologia (ou exigem waiver de Diretor).
        </p>
      </div>

      {/* Score card */}
      <Card style={{ marginBottom: 24 }}>
        {isLoading ? (
          <LoadingSpinner label="Calculando readiness..." />
        ) : (
          <div className="flex items-center gap-6">
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                border: `4px solid ${scoreColor}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
              role="meter"
              aria-valuenow={score}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Score de prontidão: ${score}%`}
            >
              <span style={{ fontSize: 22, fontWeight: 700, color: scoreColor }}>
                {Math.round(score)}%
              </span>
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: C.neutral, marginBottom: 4 }}>
                {score >= 80 ? 'Pronto para execução' :
                 score >= 50 ? 'Prontidão parcial'    :
                               'Configuração incompleta'}
              </div>
              <div style={{ fontSize: 13, color: C.neutralDim }}>
                {hasBlocker
                  ? 'Critérios impeditivos pendentes bloqueiam o Gate de Tecnologia.'
                  : 'Você pode avançar. Complete os itens parciais antes do gate.'}
              </div>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              {score >= 80 ? (
                <Badge color={C.cyber} light>PRONTO</Badge>
              ) : (
                <Badge color={hasBlocker ? C.hot : C.digital} light>
                  {hasBlocker ? 'BLOQUEADO' : 'PARCIAL'}
                </Badge>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Erros de query */}
      {hasError && (
        <div style={{ marginBottom: 16 }}>
          <QueryError
            error={projectQuery.error ?? reposQuery.error ?? contextsQuery.error}
            onRetry={() => {
              projectQuery.refetch?.();
              reposQuery.refetch?.();
            }}
          />
        </div>
      )}

      {/* Critérios */}
      {!isLoading && (
        <Card style={{ marginBottom: 20 }}>
          {CRITERIA.map((c) => (
            <CriterionRow key={c.id} {...c} onAjustar={goToStep} />
          ))}
        </Card>
      )}

      {/* Resolver itens parciais — com IA ou com o próprio texto (sempre aceito) */}
      {!isLoading && (!hasDocs || !hasSources) && (
        <Card style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.neutralDim, marginBottom: 4, letterSpacing: '0.04em' }}>
            RESOLVER ITENS PARCIAIS
          </div>
          <p style={{ fontSize: 12.5, color: C.neutralMuted, margin: '0 0 6px' }}>
            Deixe a IA preencher a partir do escopo, ou escreva você mesmo. O que você escolher é aceito
            e o critério passa a contar 100% (sobe o score).
          </p>
          {!hasDocs && (
            <ResolveCriterion
              label="Documentos & Histórias"
              hint="Nenhum documento enviado. Gere um doc de histórias/contexto com IA (revise antes), ou escreva o seu."
              onGenerate={generateScopeText}
              onSave={saveDocsText}
              saving={addDoc.isPending}
              error={addDoc.isError || suggestScope.isError}
            />
          )}
          {!hasSources && (
            <ResolveCriterion
              label="Data Sources de Negócio"
              hint="Nenhuma fonte de negócio cadastrada. Gere um contexto com IA (revise antes), ou escreva o seu."
              onGenerate={generateScopeText}
              onSave={saveSourcesText}
              saving={addSource.isPending}
              error={addSource.isError}
            />
          )}
        </Card>
      )}

      {/* O que compõe o score — itens já presentes (o que "resolveu" cada critério) */}
      {!isLoading && (docsCount > 0 || sourcesCount > 0 || contextCount > 0 || repoIndexed) && (
        <Card style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.neutralDim, marginBottom: 10, letterSpacing: '0.04em' }}>
            O QUE JÁ COMPÕE O SCORE
          </div>
          {docsCount > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12.5, color: C.neutral, fontWeight: 600, marginBottom: 4 }}>
                Documentos & Histórias ({docsCount})
              </div>
              <ul style={{ margin: 0, paddingLeft: 16, color: C.neutralDim, fontSize: 12.5 }}>
                {(docsQuery.data ?? []).slice(0, 12).map((d, i) => (
                  <li key={d.id ?? i} style={{ margin: '2px 0' }}>{d.filename || d.name || 'documento'}</li>
                ))}
              </ul>
            </div>
          )}
          {sourcesCount > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12.5, color: C.neutral, fontWeight: 600, marginBottom: 4 }}>
                Data Sources de Negócio ({sourcesCount})
              </div>
              <ul style={{ margin: 0, paddingLeft: 16, color: C.neutralDim, fontSize: 12.5 }}>
                {bizSources.slice(0, 12).map((s, i) => (
                  <li key={s.id ?? i} style={{ margin: '2px 0' }}>
                    {s.name || 'fonte'}{s.type ? ` · ${String(s.type).toUpperCase()}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {contextCount > 0 && (
            <div style={{ fontSize: 12.5, color: C.neutralDim }}>
              Contexto & Discovery: {contextCount} nota(s) salvas.
            </div>
          )}
          {repoIndexed && (
            <div style={{ fontSize: 12.5, color: C.neutralDim, marginTop: contextCount > 0 ? 6 : 0 }}>
              Repositório conectado (BROWNFIELD): código-fonte indexado cobre
              contexto, histórias e o entendimento das fontes de dados.
            </div>
          )}
          <p style={{ fontSize: 11.5, color: C.neutralMuted, margin: '8px 0 0' }}>
            Cada critério com item(ns) conta 100% do peso. O que você adicionou (por IA ou pelo seu texto) aparece aqui.
          </p>
        </Card>
      )}

      {/* Bloqueio informativo */}
      {hasBlocker && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            padding: '12px 16px',
            borderRadius: 8,
            backgroundColor: `${C.hot}11`,
            border: `1px solid ${C.hot}44`,
            fontSize: 13,
            color: C.neutralDim,
            marginBottom: 24,
          }}
        >
          <span style={{ color: C.hot, fontWeight: 600 }}>Critério impeditivo pendente. </span>
          Volte às etapas anteriores para completar os critérios impeditivos — ou, como
          alternativa, um <strong>Diretor</strong> pode aprovar um <strong>waiver</strong> (risco aceito).
          <div style={{ marginTop: 12 }}>
            <textarea value={waiverReason} onChange={(e) => setWaiverReason(e.target.value)}
              placeholder="Justificativa do waiver (obrigatória) — risco aceito pelo Diretor"
              rows={2} style={{ width: '100%', background: C.blackElev2, color: C.neutral,
                border: `1px solid ${C.blackBorder}`, borderRadius: 6, padding: 10, fontSize: 13, resize: 'vertical' }} />
            <button
              onClick={() => waiverReason.trim() && waiverMut.mutate(
                { decision: 'waiver', waiver_reason: waiverReason },
                { onSuccess: () => onNext && onNext() },
              )}
              disabled={!waiverReason.trim() || waiverMut.isPending}
              style={{ marginTop: 8, background: 'transparent', color: C.hot, border: `1px solid ${C.hot}`,
                borderRadius: 7, padding: '8px 14px', fontSize: 13, fontWeight: 600,
                cursor: waiverReason.trim() ? 'pointer' : 'default', opacity: waiverReason.trim() ? 1 : 0.5 }}>
              {waiverMut.isPending ? 'Registrando waiver…' : 'Waiver de Diretor (aceitar risco e avançar)'}
            </button>
            {waiverMut.isError && <div style={{ color: C.hot, fontSize: 12, marginTop: 6 }}>Falha ao registrar waiver.</div>}
          </div>
        </div>
      )}

      {/* Nota */}
      <div
        style={{
          padding: '10px 14px',
          borderRadius: 6,
          backgroundColor: `${C.cyber}08`,
          border: `1px solid ${C.blackBorder}`,
          fontSize: 12,
          color: C.neutralDim,
          marginBottom: 28,
          lineHeight: 1.5,
        }}
        role="note"
      >
        <span style={{ color: C.cyber }}>Readiness real. </span>
        Este score reflete o que foi configurado e salvo nas etapas anteriores —
        sem dados mockados.
      </div>

      {/* Navegação */}
      <div className="flex items-center justify-between">
        <Btn variant="ghost" size="md" onClick={onBack}>
          Voltar
        </Btn>
        <Btn
          variant="primary"
          size="lg"
          icon={ShieldCheck}
          onClick={onNext}
          disabled={hasBlocker}
          title={hasBlocker ? 'Resolva os critérios impeditivos (ou registre um waiver) antes do gate' : undefined}
        >
          Ir para o Gate de Tecnologia
        </Btn>
      </div>
    </div>
  );
}
